"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProduction = exports.PRODUCTION_SCALE_N = exports.PRODUCTION_SCALE = void 0;
const react_1 = require("react");
const values_1 = __importDefault(require("../values"));
const smap_1 = require("../smap");
const State_1 = require("../typeDefs/State");
const assembly_1 = require("../assembly");
const bigmath_1 = require("../bigmath");
const useGameState_1 = require("./useGameState");
const numberFormatter_1 = require("../numberFormatter");
exports.PRODUCTION_SCALE = 10000;
exports.PRODUCTION_SCALE_N = BigInt(exports.PRODUCTION_SCALE);
const updateTimestamps = [];
function useProduction(ticksPerSecond) {
    const { stateRef, addToTotal, dispatchAction, getPower, getProductionProgress, hasStorageCapacity, saveGame, calculateStorage, } = (0, useGameState_1.useGameState)();
    const [fps, setFps] = (0, react_1.useState)(0);
    const [, forceUpdate] = (0, react_1.useReducer)(x => x + 1, 0);
    function checkPowerConsumption(itemName, building) {
        const [power, state] = getPower(itemName, building);
        if (state === State_1.PRODUCTION_NO_POWER) {
            const r = values_1.default.buildingPowerRequirementsPerSecond[building];
            if ((0, assembly_1.checkAmounts)(stateRef.current.amountThatWeHave, r)) {
                stateRef.current.powerConsumptionProgress[itemName][building] = 0;
                stateRef.current.powerConsumptionState[itemName][building] =
                    State_1.PRODUCTION_HAS_POWER;
                return true;
            }
            return false;
        }
        else if (power >= ticksPerSecond) {
            stateRef.current.powerConsumptionState[itemName][building] =
                State_1.PRODUCTION_NO_POWER;
            return checkPowerConsumption(itemName, building);
        }
        else {
            stateRef.current.powerConsumptionProgress[itemName][building] =
                power + 1;
            return true;
        }
    }
    function doProduction(timeStep) {
        const { assemblers, amountThatWeHave, productionProgress, productionState, storage, } = stateRef.current;
        (0, smap_1.keys)(assemblers)
            .sort()
            .forEach((itemName) => {
            (0, smap_1.forEach)(assemblers[itemName], (assemblerCount, assemblerName) => {
                if (stateRef.current.disabledRecipes[itemName] === true) {
                    return;
                }
                let amountAddPerTick = (0, bigmath_1.scaleBigInt)(assemblerCount, exports.PRODUCTION_SCALE * values_1.default.assemblerSpeeds[assemblerName] * timeStep /
                    values_1.default.timePerRecipe[itemName]);
                amountAddPerTick = (0, bigmath_1.scaleBigInt)(amountAddPerTick, values_1.default.calculateBoost(assemblerName, stateRef.current));
                let [time, state] = getProductionProgress(itemName, assemblerName);
                if (!storage[itemName])
                    storage[itemName] = {};
                // there's probably a better way to organize this code
                if (state === State_1.PRODUCTION_OUTPUT_BLOCKED) {
                    if (addToTotal(itemName, (0, bigmath_1.NumToBig)(1))) {
                        time = 0n;
                        state = State_1.PRODUCTION_NO_INPUT;
                    }
                    else {
                        return;
                    }
                }
                const hadNoInput = state === State_1.PRODUCTION_NO_INPUT;
                const hasPower = checkPowerConsumption(itemName, assemblerName);
                if (hasPower && hadNoInput) {
                    const result = (0, assembly_1.consumeMaterialsFromRecipe)(itemName, amountThatWeHave, (0, bigmath_1.NumToBig)(1));
                    if (!result)
                        state = State_1.PRODUCTION_NO_INPUT;
                    else if (hadNoInput) {
                        time = (0, bigmath_1.scaleBigInt)(amountAddPerTick, -fps);
                        state = State_1.PRODUCTION_RUNNING;
                    }
                }
                if (state === State_1.PRODUCTION_RUNNING) {
                    if (!hasPower) {
                        return;
                    }
                    doPowerConsumption(itemName, assemblerName);
                    time += amountAddPerTick;
                    const amountToProduce = time / exports.PRODUCTION_SCALE_N;
                    if ((0, bigmath_1.bigGtE)(amountToProduce, 1)) {
                        if (addToTotal(itemName, (0, bigmath_1.bigFloor)(amountToProduce))) {
                            time -= amountToProduce * exports.PRODUCTION_SCALE_N;
                            if (!(0, assembly_1.consumeMaterialsFromRecipe)(itemName, amountThatWeHave, (0, bigmath_1.bigFloor)(amountToProduce))) {
                                state = State_1.PRODUCTION_NO_INPUT;
                            }
                        }
                        else {
                            state = State_1.PRODUCTION_OUTPUT_BLOCKED;
                        }
                    }
                }
                productionProgress[itemName][assemblerName] = time;
                productionState[itemName][assemblerName] = state;
            });
        });
        return {
            amountThatWeHave,
        };
    }
    const canMakeItemByHand = (0, react_1.useCallback)((itemName) => {
        if (!stateRef.current.visible[itemName])
            return false;
        if (values_1.default.requiredBuildings(itemName).includes("by-hand") === false)
            return null;
        if ((0, bigmath_1.bigLt)((0, assembly_1.howManyRecipesCanBeMade)(itemName, stateRef.current.amountThatWeHave), 1))
            return false;
        return (0, bigmath_1.bigGtE)(hasStorageCapacity(itemName), 1);
    }, []);
    const updateUI = (0, react_1.useCallback)(() => {
        const now = new Date().getTime();
        while (updateTimestamps[0] < now - 1000) {
            updateTimestamps.shift();
        }
        setFps(updateTimestamps.length);
        forceUpdate();
        const before = stateRef.current.lastUIUpdateTimestamp === 0
            ? now - 1
            : stateRef.current.lastUIUpdateTimestamp;
        const timeDiff = Math.min(1, (now - before) / 1000);
        stateRef.current.lastUIUpdateTimestamp = now;
        stateRef.current.ticksSinceLastUIUpdate = 0;
        stateRef.current.timeSpentPlaying += timeDiff;
    }, []);
    function doPowerConsumption(itemName, building) {
        const [power, state] = getPower(itemName, building);
        if (state === State_1.PRODUCTION_HAS_POWER && power >= ticksPerSecond) {
            let amount = stateRef.current.assemblers[itemName][building];
            const r = values_1.default.buildingPowerRequirementsPerSecond[building];
            (0, assembly_1.consumeMaterials)(undefined, stateRef.current.amountThatWeHave, r, amount);
            checkPowerConsumption(itemName, building);
        }
    }
    const clearVisibles = () => {
        stateRef.current.visible = {};
        const { timeUnlockedAt, acknowledged, assemblers, visible, } = stateRef.current;
        (0, assembly_1.checkVisible)(stateRef.current, doAction);
        (0, assembly_1.hideTheHideOnBuyItems)(stateRef.current);
        (0, smap_1.keys)(timeUnlockedAt).forEach(itemName => {
            if (visible[itemName] === undefined) {
                delete timeUnlockedAt[itemName];
                delete acknowledged[itemName];
            }
            if (visible[itemName] === false) {
                delete assemblers[itemName];
            }
        });
    };
    (0, react_1.useEffect)(() => {
        const i = setInterval(() => {
            const now = new Date().getTime();
            const timeDiff = Math.min(1, (now - stateRef.current.lastTickTimestamp) / 1000);
            doProduction(timeDiff);
            stateRef.current.lastTickTimestamp = now;
            stateRef.current.ticksSinceLastUIUpdate++;
            updateTimestamps.push(now);
        }, 1000 / ticksPerSecond);
        return () => {
            clearInterval(i);
        };
    }, []);
    const UI_UPDATE_FREQUENCY_PER_SEC = 2;
    (0, react_1.useEffect)(() => {
        const i = setInterval(() => {
            updateUI();
        }, 1000 / UI_UPDATE_FREQUENCY_PER_SEC);
        return () => {
            clearInterval(i);
        };
    }, []);
    (0, react_1.useEffect)(() => {
        document.game ??= {};
        document.game.setAmount = (amount, item) => {
            doAction({
                action: 'set-amount',
                amount,
                item,
            });
        };
        document.game.clearVisibles = clearVisibles;
        setInterval(() => saveGame(stateRef.current), 10 * 1000);
    }, []);
    function doAction(action) {
        dispatchAction(action);
        updateUI();
    }
    (0, assembly_1.checkVisible)(stateRef.current, doAction);
    (0, numberFormatter_1.setMode)(stateRef.current.numberFormatMode ?? numberFormatter_1.NumberFormat.SUFFIX);
    return {
        ...stateRef.current,
        state: {
            ...stateRef.current,
            calculateStorage,
        },
        fps,
        canMakeItemByHand,
        doAction,
    };
}
exports.useProduction = useProduction;
